import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Camera, ChatCircleDots, CloudArrowUp, Gauge, ShieldWarning, StopCircle, X, type Icon } from '@phosphor-icons/react';
import { LIMITS, TILE, TILE_HINT, TILE_LABEL, outboxVerdict, type SessionUser, type Tile } from '@helpdesk/shared';
import { ApiRequestError, isOfflineError, postJson, uploadFile } from '../api/client.ts';
import { resizePhoto } from '../lib/image.ts';
import { acknowledgeSent, queueReport, type PendingReport, type QueuedReport } from '../lib/outbox.ts';
import { forgetName, rememberName, rememberedName } from '../lib/reporter-name.ts';
import { useOutbox } from './outbox-panel.tsx';
import { Button, ErrorNote, Input, Notice, Textarea } from './ui.tsx';

/**
 * Das Meldeformular (F16, §10.2) – gleich für Web, QR, Schnellmeldung und Kiosk.
 * Pflicht: eine Kachel und ein Satz ODER ein Foto; anonym zusätzlich der Name.
 * Zielzeit vom Scan bis zum Absenden: unter 20 Sekunden.
 */
const TILE_ICON: Record<Tile, Icon> = {
  safety: ShieldWarning,
  stop: StopCircle,
  impaired: Gauge,
  other: ChatCircleDots,
};

export type ReportTarget =
  | { kind: 'web'; machineId: string | null; machineCode?: string; withoutMachine: boolean; origin: 'web' | 'qr'; owner: SessionUser }
  | { kind: 'quick'; code: string }
  | { kind: 'kiosk'; token: string; machineId: string };

export type ReportResult = {
  display: string;
  /** Im Ausgang: die ganze Meldung oder, mit `display`, nur noch ihre Fotos. */
  queued: { id: string; kind: ReportTarget['kind'] } | null;
  photoErrors: number;
  /** Wie es weitergeht, siehe `followUpText`. */
  followUp: string;
};

const NOTIFIED = 'Die Instandhaltung kümmert sich und meldet sich bei Ihnen.';
const LOOKUP = 'Die Instandhaltung kümmert sich. Den Stand sehen Sie';

/**
 * Die Zusage folgt `reporterRecipient` im Server: Rückmeldung bekommen nur Microsoft-Konten und die Firmen-Mail,
 * die der Server beim Annehmen prüft. Alle anderen holen sich den Stand selbst, und wo, hängt am Weg.
 * Gemeldet wird an jeder Maschine, „Tickets“ zeigt aber nur die eigenen Werke (`workerScope`). Die Maschinenseite gilt werksübergreifend.
 */
function followUpText(target: ReportTarget, email: string): string {
  if (target.kind === 'kiosk') return `${LOOKUP} unter dieser Nummer hier am Tablet.`;
  if (target.kind === 'quick') return email ? NOTIFIED : `${LOOKUP} unter dieser Nummer, wenn Sie den QR-Code erneut scannen.`;
  if (target.owner.authSource === 'entra') return NOTIFIED;
  if (target.owner.role === 'reporter') return `${LOOKUP} unter „Meine Meldungen“.`;
  return target.withoutMachine ? `${LOOKUP} unter „Tickets“.` : `${LOOKUP} unter dieser Nummer auf der Maschinenseite.`;
}

/** Folgeschritte in `ReportDone`. Lange Beschriftungen brechen um: Bei 375 px ragten sie sonst aus der Karte. */
export const NEXT_STEP_LINK = 'ad-button-secondary ad-button-lg whitespace-normal no-underline';

export function ReportForm({
  target,
  anonymous,
  machineLabel,
  safetyNote,
  sharedDevice,
  onDone,
}: {
  target: ReportTarget;
  anonymous: boolean;
  machineLabel?: string | null;
  /** `/melden` zeigt ihn selbst: Dort steht er auch, wenn das Formular an einer stillgelegten Maschine verborgen bleibt. */
  safetyNote?: string | null | undefined;
  /** Die Angabe des Servers. Ohne Netz fehlt sie. */
  sharedDevice?: boolean | undefined;
  onDone: (result: ReportResult) => void;
}) {
  const [tile, setTile] = useState<Tile | null>(null);
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [photos, setPhotos] = useState<Blob[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [queueFailed, setQueueFailed] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p)), [previews]);

  // `/melden` lässt das Formular beim Wechsel der Maschine stehen: Eine Ablehnung wie „stillgelegt“ galt der alten.
  const machineId = target.kind === 'web' ? target.machineId : null;
  useEffect(() => setError(null), [machineId]);

  // Fehlt die Angabe, bleibt das Feld leer: Lieber einmal tippen als ein fremder Name auf der Meldung. Kommt sie nach, wird das nachgeholt.
  const ownDevice = target.kind === 'quick' && sharedDevice === false;
  const foreignDevice = target.kind === 'kiosk' || sharedDevice === true;
  useEffect(() => {
    if (ownDevice) setName((typed) => typed || rememberedName());
    if (foreignDevice) forgetName();
  }, [ownDevice, foreignDevice]);

  const addPhotos = async (files: FileList | null) => {
    if (!files) return;
    const room = LIMITS.photosOnReport - photos.length;
    const picked = Array.from(files).slice(0, room);
    const resized = await Promise.all(picked.map((f) => resizePhoto(f)));
    setPhotos((p) => [...p, ...resized]);
    setPreviews((p) => [...p, ...resized.map((b) => URL.createObjectURL(b))]);
  };

  const removePhoto = (i: number) => {
    setPhotos((p) => p.filter((_, idx) => idx !== i));
    setPreviews((p) => p.filter((_, idx) => idx !== i));
  };

  const valid = Boolean(tile) && (description.trim().length >= 3 || photos.length > 0) && (!anonymous || name.trim().length >= 2);

  const submit = async () => {
    if (!tile) return;
    setBusy(true);
    setError(null);
    setQueueFailed(false);
    const base = { tile, description: description.trim() || null, photoCount: photos.length };
    let endpoint: string;
    let body: Record<string, unknown>;
    if (target.kind === 'web') {
      endpoint = '/api/tickets';
      body = { ...base, machineId: target.machineId, machineCode: target.machineCode, withoutMachine: target.withoutMachine, origin: target.origin };
    } else if (target.kind === 'quick') {
      endpoint = '/api/quick/report';
      body = { ...base, code: target.code, name: name.trim(), email: email.trim() || null, website: honeypot };
    } else {
      endpoint = `/api/kiosk/${target.token}/report`;
      body = { ...base, machineId: target.machineId, name: name.trim(), website: honeypot };
    }

    if (ownDevice) rememberName(name.trim());

    const followUp = followUpText(target, email.trim());
    const queued ={ kind: target.kind, endpoint, body, machineLabel, ...(target.kind === 'web' ? { ownerUserId: target.owner.id } : {}) };
    let created: { id?: string; ticketId?: string; display: string; uploadToken?: string };
    try {
      created = await postJson(endpoint, body);
    } catch (err) {
      // Liegt es am Netz oder an der Sitzung, gilt dasselbe wie ohne Netz: Der Ausgang hält die Meldung, etwa bis das Firmennetz wieder da ist.
      if (isOfflineError(err) || (err instanceof ApiRequestError && outboxVerdict(err.status, err.code) === 'hold')) {
        let id: string;
        try {
          id = await queueReport({ ...queued, photos });
        } catch {
          setQueueFailed(true);
          setBusy(false);
          return;
        }
        setBusy(false);
        onDone({ display: '', queued: { id, kind: target.kind }, photoErrors: 0, followUp });
        return;
      }
      setError(err);
      setBusy(false);
      return;
    }

    // Fotos einzeln hochladen – jeder Request bleibt klein (§4.6).
    let photoErrors = 0;
    let queuedPhotos: ReportResult['queued'] = null;
    const ticketId = created.ticketId ?? created.id!;
    for (let i = 0; i < photos.length; i++) {
      try {
        if (target.kind === 'web') {
          await uploadFile(`/api/tickets/${ticketId}/attachments`, photos[i]!, `foto-${i + 1}.jpg`);
        } else {
          await uploadFile(`/api/quick/tickets/${ticketId}/attachments`, photos[i]!, `foto-${i + 1}.jpg`, { name: name.trim() }, { 'x-upload-token': created.uploadToken ?? '' });
        }
      } catch (err) {
        if (isOfflineError(err)) {
          try {
            const id = await queueReport({
              ...queued,
              photos: photos.slice(i),
              ticketId,
              ...(created.uploadToken ? { uploadToken: created.uploadToken } : {}),
              number: created.display,
              uploaded: 0,
            });
            queuedPhotos = { id, kind: target.kind };
          } catch {
            // Das Ticket besteht: Der Melder bekommt seine Nummer, nur diese Fotos sind verloren.
            photoErrors += photos.length - i;
          }
          break;
        }
        photoErrors++;
      }
    }
    setBusy(false);
    onDone({ display: created.display, queued: queuedPhotos, photoErrors, followUp });
  };

  return (
    <div className="space-y-5">
      {machineLabel ? (
        <div className="border-l-4 border-steel-800 bg-white px-4 py-3">
          <div className="ad-caption">Maschine</div>
          <div className="text-[18px] font-semibold text-steel-900">{machineLabel}</div>
        </div>
      ) : null}

      {safetyNote ? (
        <div className="border-l-4 border-arnold-red bg-arnold-red-tint px-4 py-3 text-[16px] text-steel-900">
          <strong>Sicherheitshinweis:</strong> {safetyNote}
        </div>
      ) : null}

      <fieldset>
        <legend className="ad-label">Was ist los?</legend>
        <div className="grid grid-cols-2 gap-3">
          {TILE.map((t) => {
            const TileIcon = TILE_ICON[t];
            const on = tile === t;
            return (
              <button key={t} type="button" aria-pressed={on} onClick={() => setTile(t)} className={`ad-tile ${on ? 'ad-tile-active' : ''}`}>
                <TileIcon size={32} weight={on ? 'fill' : 'duotone'} className={t === 'safety' || on ? 'text-arnold-red' : 'text-steel-700'} aria-hidden />
                <span>
                  <span className="block font-display text-[20px] leading-tight font-bold text-steel-900">{TILE_LABEL[t]}</span>
                  <span className="mt-0.5 block text-[14px] leading-snug text-steel-700">{TILE_HINT[t]}</span>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <div>
        <span className="ad-label">Foto (optional, bis zu {LIMITS.photosOnReport})</span>
        <div className="flex flex-wrap items-center gap-3">
          {previews.map((src, i) => (
            <div key={src} className="relative">
              <img src={src} alt={`Foto ${i + 1}`} className="h-24 w-24 border border-[var(--border-default)] object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="absolute -top-2.5 -right-2.5 inline-flex h-9 w-9 cursor-pointer items-center justify-center bg-steel-900 text-white"
                aria-label={`Foto ${i + 1} entfernen`}
              >
                <X size={18} weight="bold" />
              </button>
            </div>
          ))}
          {photos.length < LIMITS.photosOnReport ? (
            <Button variant="outline" size="lg" onClick={() => fileInput.current?.click()}>
              <Camera size={24} />
              Foto aufnehmen
            </Button>
          ) : null}
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => {
              void addPhotos(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        <p className="mt-1.5 text-[14px] text-[var(--text-muted)]">Bitte nur die Maschine fotografieren, keine Personen.</p>
      </div>

      <Textarea
        label="Ein Satz genügt"
        value={description}
        onChange={setDescription}
        rows={3}
        maxLength={LIMITS.descriptionMaxChars}
        placeholder="z. B. Fehler 17 auf dem Display, Schneidkopf fährt nicht"
      />

      {anonymous ? (
        <>
          <Input label="Ihr Name" required value={name} onChange={setName} placeholder="Vor- und Nachname" autoComplete={ownDevice ? 'name' : 'off'} />
          {target.kind === 'quick' ? (
            <Input
              label="Firmen-Mail für Rückmeldungen (optional)"
              type="email"
              inputMode="email"
              value={email}
              onChange={setEmail}
              placeholder="vorname.nachname@…"
            />
          ) : null}
          {/* Honigtopf: für Menschen unsichtbar, Bots füllen ihn aus. */}
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="website" />
        </>
      ) : null}

      {error ? <ErrorNote error={error} /> : null}
      {queueFailed ? (
        <div role="alert">
          <Notice tone="warn">Keine Verbindung und Speichern nicht möglich. Bitte den Text notieren oder die Instandhaltung direkt ansprechen.</Notice>
        </div>
      ) : null}
      {!valid && tile ? <Notice>Bitte einen kurzen Satz schreiben oder ein Foto anhängen{anonymous ? ' und Ihren Namen angeben' : ''}.</Notice> : null}

      <button type="button" className="ad-big-button disabled:cursor-not-allowed disabled:opacity-50" disabled={!valid || busy} onClick={() => void submit()}>
        {busy ? 'Wird gesendet …' : 'Meldung absenden'}
      </button>
    </div>
  );
}

const SAVED = 'Auf diesem Gerät gespeichert, noch nicht gesendet.';
const CONFIRMS = 'Erst die Ticketnummer bestätigt den Eingang.';
const URGENT = 'Dringend? Bitte die Instandhaltung direkt ansprechen.';

/** Der Weg entscheidet, was stimmt: Eine Schnellmeldung auf dem Handy sendet nur, solange die Seite offen ist. */
const QUEUED_TEXT: Record<ReportTarget['kind'], string> = {
  web: `${SAVED} Sie wird gesendet, sobald die App wieder Verbindung hat. ${CONFIRMS}`,
  quick: `${SAVED} Bitte diese Seite wieder öffnen, sobald Netz da ist. ${CONFIRMS} ${URGENT}`,
  kiosk: `${SAVED} Sie wird gesendet, sobald dieses Tablet wieder Verbindung hat. ${CONFIRMS} ${URGENT}`,
};

/** Was mit einer Meldung ohne Nummer gerade ist. Ohne Eintrag wurde sie verworfen oder ist verfallen. */
function QueuedStatus({ kind, report }: { kind: ReportTarget['kind']; report: PendingReport | undefined }) {
  const state = report?.state;
  if (!report || state === 'expired') {
    return (
      <>
        <p className="font-display text-[24px] font-bold text-steel-900">Meldung nicht gesendet</p>
        <p className="text-[var(--text-muted)]">Sie liegt nicht mehr auf diesem Gerät. Bitte bei Bedarf neu melden.</p>
      </>
    );
  }
  const anonymous = kind !== 'web';
  return (
    <>
      <CloudArrowUp size={44} className={`mx-auto ${state === 'rejected' ? 'text-error' : 'text-warn'}`} aria-hidden />
      <p className="font-display text-[24px] font-bold text-steel-900">{state === 'rejected' ? 'Meldung abgelehnt' : 'Meldung gespeichert'}</p>
      <p className="text-[var(--text-muted)]">
        {state === 'rejected'
          ? anonymous
            ? 'Der Server hat sie nicht angenommen. Bitte die Instandhaltung direkt ansprechen.'
            : 'Der Server hat sie nicht angenommen. Grund und „Erneut senden“ stehen in der App oben im Ausgang.'
          : state === 'held' && !anonymous
            ? `${SAVED} Sie wird gesendet, sobald Sie wieder angemeldet sind. ${CONFIRMS}`
            : QUEUED_TEXT[kind]}
      </p>
      {report.reason ? <Notice tone="warn">{report.reason}</Notice> : null}
    </>
  );
}

/** Ohne Konto hängen die Fotos am Upload-Token, das 15 Minuten gilt, und nur eine offene Seite sendet sie. */
function openPhotosText({ kind, state }: QueuedReport): string {
  if (kind !== 'web') return 'Die Meldung selbst ist eingegangen. Die Fotos werden nur in den nächsten Minuten nachgesendet, solange diese Seite offen ist.';
  return state === 'held' ? 'Sie werden nachgesendet, sobald Sie wieder angemeldet sind.' : 'Sie werden nachgesendet.';
}

/** Bestätigung nach dem Absenden (F22): Nummer groß, dazu wie es weitergeht. Eine gespeicherte Meldung wechselt live auf ihre Nummer. */
export function ReportDone({ result, onAgain, extra }: { result: ReportResult; onAgain?: () => void; extra?: (display: string) => ReactNode }) {
  const { reports, sent } = useOutbox();
  const id = result.queued?.id;
  const report = reports.find((r) => r.id === id);
  const sentNumber = sent.find((s) => s.id === id)?.number;
  const display = result.display || sentNumber || report?.number || '';
  const open = report && report.state !== 'expired' ? report : undefined;
  const openPhotos = open ? open.photos.length - (open.uploaded ?? 0) : 0;
  const photoErrors = result.photoErrors + (open?.failedPhotos?.length ?? 0);

  useEffect(() => {
    if (id && sentNumber) acknowledgeSent(id);
  }, [id, sentNumber]);

  return (
    <div className="ad-card space-y-4 text-center">
      {display ? (
        <>
          <p className="ad-caption">Ihre Meldung</p>
          <p className="font-mono text-5xl font-medium text-arnold-red">{display}</p>
          <p className="text-[18px] text-steel-900">Danke! {result.followUp}</p>
          {open && openPhotos ? (
            <Notice tone={open.kind === 'web' ? 'info' : 'warn'}>
              {openPhotos} Foto(s) noch nicht übertragen. {openPhotosText(open)}
            </Notice>
          ) : null}
          {photoErrors ? <Notice tone="warn">{photoErrors} Foto(s) konnten nicht übertragen werden.</Notice> : null}
        </>
      ) : result.queued ? (
        <QueuedStatus kind={result.queued.kind} report={report} />
      ) : null}
      <div className="flex flex-col items-center gap-3">
        {extra?.(display)}
        {onAgain ? (
          <Button variant="outline" onClick={onAgain}>
            Weitere Störung melden
          </Button>
        ) : null}
      </div>
    </div>
  );
}
